import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    buildCourseSlots,
    dropOutsideRange,
    formatClock,
    openSessionsInRange,
    selectionOutsideRange,
    sessionInRange,
    slotSelectionState,
    toggleAllSlots,
    toggleSlotSelection,
} from '../../../utils/courseSessionPattern';

function slotClass(state) {
    if (state === 'all') {
        return 'border-indigo-600 bg-indigo-600 text-white shadow-sm';
    }
    if (state === 'partial') {
        return 'border-indigo-400 bg-indigo-50 text-indigo-950 dark:border-indigo-400 dark:bg-indigo-950/50 dark:text-indigo-100';
    }
    if (state === 'empty') {
        return 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-500';
    }
    return 'border-gray-200 bg-white text-gray-900 hover:border-indigo-300 hover:bg-indigo-50/60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-indigo-400 dark:hover:bg-indigo-950/30';
}

export default function WeekdayPatternPicker({
    sessions,
    selectedIds,
    onChange,
    rangeFrom,
    rangeTo,
    isBlocked,
}) {
    const { t } = useTranslation();
    const blocked = isBlocked || (() => false);
    const options = { from: rangeFrom, to: rangeTo, isBlocked: blocked };
    const slots = useMemo(() => buildCourseSlots(sessions), [sessions]);
    const openInPeriod = openSessionsInRange(sessions, options);
    const allState = slotSelectionState(sessions, selectedIds, options);
    const outside = selectionOutsideRange(sessions, selectedIds, rangeFrom, rangeTo);

    const handleSlot = (slot) => {
        onChange(toggleSlotSelection(selectedIds, slot.sessions, options));
    };

    const handleAll = () => {
        onChange(toggleAllSlots(selectedIds, sessions, options));
    };

    if (slots.length === 0) {
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('gym.bulkEnrollment.courses.noLessonsInPeriod')}
            </p>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={handleAll}
                    disabled={openInPeriod.length === 0}
                    aria-pressed={allState === 'all'}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                        allState === 'all'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700'
                    }`}
                >
                    {allState === 'all'
                        ? t('gym.bulkEnrollment.courses.clearWeekdays')
                        : t('gym.bulkEnrollment.courses.allWeekdays')}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('gym.bulkEnrollment.courses.patternHint')}
                </p>
            </div>

            <div className="flex flex-wrap gap-2">
                {slots.map((slot) => {
                    const state = slotSelectionState(slot.sessions, selectedIds, options);
                    const inRange = slot.sessions.filter((session) =>
                        sessionInRange(session, rangeFrom, rangeTo)
                    );
                    const open = openSessionsInRange(slot.sessions, options);
                    const excluded = inRange.length - open.length;
                    const day = t(`gym.bulkEnrollment.courses.weekdays.${slot.weekdayKey}`);
                    const fullDay = t(`gym.courses.addRecurrenceModal.daysOfWeek.${slot.weekdayKey}`);
                    const time = `${formatClock(slot.startTime)}–${formatClock(slot.endTime)}`;
                    const picked = open.filter((session) =>
                        (selectedIds || []).map(String).includes(String(session.id))
                    ).length;

                    return (
                        <button
                            key={slot.key}
                            type="button"
                            disabled={state === 'empty'}
                            aria-pressed={state === 'all'}
                            aria-label={t('gym.bulkEnrollment.courses.slotAria', {
                                day: fullDay,
                                time,
                                count: open.length,
                            })}
                            onClick={() => handleSlot(slot)}
                            className={`min-w-[8.5rem] rounded-xl border px-3 py-2.5 text-left transition-colors ${slotClass(state)}`}
                        >
                            <span className="block text-sm font-semibold">{day}</span>
                            <span className={`mt-0.5 block text-xs ${state === 'all' ? 'text-indigo-100' : 'text-current opacity-75'}`}>
                                {time}
                            </span>
                            <span className={`mt-1 block text-xs font-medium ${state === 'all' ? 'text-white' : ''}`}>
                                {state === 'partial'
                                    ? t('gym.bulkEnrollment.courses.partialCount', {
                                          selected: picked,
                                          total: open.length,
                                      })
                                    : t('gym.bulkEnrollment.courses.lessons', { count: open.length })}
                            </span>
                            {excluded > 0 && (
                                <span className={`mt-0.5 block text-[11px] ${state === 'all' ? 'text-indigo-100' : 'text-amber-700 dark:text-amber-300'}`}>
                                    {t('gym.bulkEnrollment.courses.excluded', { count: excluded })}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {outside.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                    <span>
                        {t('gym.bulkEnrollment.courses.outsideRange', { count: outside.length })}
                    </span>
                    <button
                        type="button"
                        onClick={() =>
                            onChange(dropOutsideRange(selectedIds, sessions, rangeFrom, rangeTo))
                        }
                        className="font-semibold underline"
                    >
                        {t('gym.bulkEnrollment.courses.dropOutside')}
                    </button>
                </div>
            )}
        </div>
    );
}
